import React, { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import api, { PerguntaResponse } from 'api/Associado/duvidas';

import { IAssembleiaAssociadoEvento } from 'store/modules/AssembleiasAssociadoReducer';
import { IAuthUserReducer } from 'store/modules/AuthReducer';
import { IWSAssembleiasReducer } from 'store/modules/WSAssembleiasReducer';
import { IReducer } from 'store/Reducer';

import ErrorHandler from 'utils/ErrorHandler';

import { Button, Divider } from '@material-ui/core';
import AssembleiaCard from 'components/AssembleiaCard';
import { DialogIcon, SyncIcon } from 'components/Icons';

import Context from './Context';
import Duvidas from './Duvidas';
import Perguntar from './Perguntar';
import { Sections, duvidasInitialState, duvidasReducer } from './reducer';
import Resposta from './Resposta';
import useStyles from './style';

interface Props {
  associado: IAuthUserReducer;
  evento: IAssembleiaAssociadoEvento;
}

export default function ({ evento, associado }: Props) {
  const { t } = useTranslation();
  const classes = useStyles();
  const { assembleiasChanges } = useSelector<IReducer, IWSAssembleiasReducer>(state => state.wsAssembleias);
  const [{ perguntas, perguntaAtual, section, tab, isLoading, message, idEvento }, action] = useReducer(
    duvidasReducer,
    duvidasInitialState
  );

  const isConvocado = useMemo(() => {
    if (evento.contasDisponiveis && evento.contasDisponiveis.length > 0) {
      return evento.contasDisponiveis.some(conta => conta.idConvocado !== undefined);
    }
    return false;
  }, [evento]);

  const hasPermissionToSend = useMemo((): boolean => {
    if (!evento) return false;
    if (evento.status === 1) return associado.perfil.associadoRoute.assembleias.pre.duvidasSugestoes.participar;
    if (evento.status > 1) return associado.perfil.associadoRoute.assembleias.live.duvidasSugestoes.participar;
    return false;
  }, [evento, associado]);

  const list = () => action({ type: 'setSection', value: Sections['list'] });

  const ask = () => action({ type: 'setSection', value: Sections['ask'] });

  const answer = useCallback(
    (idDuvida: number, publica?: boolean) => {
      if (!perguntas) return;
      const lista = publica ? perguntas.publicas : perguntas.usuario;
      const found = lista.find(pergunta => pergunta.idPergunta === idDuvida);
      if (found) {
        action({ type: 'setPergunta', value: found });
        action({ type: 'setSection', value: Sections['answer'] });
      }
    },
    [perguntas]
  );

  const changeTab = (value: 0 | 1) => action({ type: 'setTab', value });

  const atualizarPergunta = useCallback(
    (changes: PerguntaResponse, flgPublica?: boolean) => {
      if (!perguntas) return;
      const _pergunta = Object.assign({}, changes);
      const isPublic = _pergunta.cpfUsuario !== associado.cpfCnpj;
      const canBeListed = !isPublic || flgPublica;
      const lista = Array.from(isPublic ? perguntas.publicas : perguntas.usuario);
      const _index = lista.findIndex(item => item.idPergunta === _pergunta.idPergunta);
      if (_index >= 0) {
        !canBeListed && lista.splice(_index, 1);
        canBeListed && (lista[_index] = _pergunta);
      } else {
        canBeListed && lista.push(_pergunta);
      }
      action({
        type: 'setPerguntas',
        value: {
          publicas: isPublic ? lista : perguntas.publicas,
          usuario: !isPublic ? lista : perguntas.usuario,
        },
      });
    },
    [perguntas, associado]
  );

  useEffect(() => {
    const carregarPerguntas = async () => {
      action({ type: 'loading' });
      const { fail, data } = await api.listar(evento.idAssembleia);
      if (fail) {
        ErrorHandler(fail);
      }
      if (data) {
        const publicas = data.filter(pergunta => pergunta.cpfUsuario !== associado.cpfCnpj);
        const usuario = data.filter(pergunta => pergunta.cpfUsuario === associado.cpfCnpj);
        action({ type: 'setPerguntas', value: { publicas, usuario } });
      }
      action({ type: 'loaded' });
    };
    if (evento.idAssembleia !== idEvento) {
      action({ type: 'setEvento', value: evento.idAssembleia });
      carregarPerguntas();
    }
  }, [associado, evento, idEvento]);

  useEffect(() => {
    if (!assembleiasChanges[evento.idAssembleia]) return;
    const newMessage = assembleiasChanges[evento.idAssembleia].messenger;
    if (newMessage && newMessage !== null) {
      action({ type: 'setMessage', value: newMessage });
    }
  }, [assembleiasChanges, evento]);

  useEffect(() => {
    if (message) {
      const _message = Object.assign({}, message);
      action({
        type: 'setMessage',
        value: null,
      });
      atualizarPergunta(
        {
          pergunta: _message.duvida,
          idPergunta: _message.idDuvidaFrequente,
          resposta: _message.resposta,
          cpfUsuario: _message.cpf,
          usuarioPergunta: _message.nomePessoaComentario,
          dataPergunta: _message.dataPergunta,
          dataResposta: '',
          lido: false,
          destaque: _message.flgDestaque,
        },
        _message.flgPublica
      );
    }
  }, [message, atualizarPergunta]);

  useEffect(() => {
    if (perguntaAtual && section === Sections.answer) {
      answer(perguntaAtual.idPergunta, perguntaAtual.cpfUsuario !== associado.cpfCnpj);
    }
  }, [perguntas, perguntaAtual, answer, associado, section]);

  return (
    <Context.Provider
      value={{
        list,
        ask,
        answer,
        perguntas,
        perguntaAtual,
        tab,
        changeTab,
        atualizarPergunta,
        cpf: associado.cpfCnpj,
      }}
    >
      <AssembleiaCard
        title={t('AssembleiaAssociadoRoute.faq.public')}
        icon={isLoading ? <SyncIcon animated /> : <DialogIcon />}
        className={classes.container}
        noPadding
      >
        <Divider />
        {section === Sections.list && <Duvidas />}
        {evento && section === Sections.ask && <Perguntar usuario={associado.cpfCnpj} idEvento={evento.idAssembleia} />}
        {section === Sections.answer && <Resposta />}
        {section !== Sections.ask && hasPermissionToSend && isConvocado && (
          <React.Fragment>
            <Divider />
            <Button
              variant="contained"
              size="large"
              color="primary"
              className={classes.buttonAsk}
              onClick={ask}
              disabled={!evento.statusChat}
            >
              {t('AssembleiaAssociadoRoute.faq.ask')}
            </Button>
          </React.Fragment>
        )}
      </AssembleiaCard>
    </Context.Provider>
  );
}
